import { z } from 'zod';

/* ----------------------------- project ----------------------------- */

/** Project keys: 2-10 uppercase letters/digits, starting with a letter (ADR-0013). */
export const PROJECT_KEY_PATTERN = /^[A-Z][A-Z0-9]{1,9}$/;
export const ProjectKeySchema = z
  .string()
  .regex(
    PROJECT_KEY_PATTERN,
    'project key: 2-10 uppercase letters or digits, starting with a letter',
  );

/** `#rgb` or `#rrggbb` colors for priorities, options and statuses. */
export const ColorSchema = z
  .string()
  .regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, 'hex color such as #1d4ed8');

export const TERMINOLOGIES = ['issue', 'request', 'task'] as const;
export const TerminologySchema = z.enum(TERMINOLOGIES);
export type Terminology = z.infer<typeof TerminologySchema>;

export const PORTAL_ACCESS_MODES = ['invite_only', 'tenant', 'public'] as const;
export const PortalAccessModeSchema = z.enum(PORTAL_ACCESS_MODES);
export type PortalAccessMode = z.infer<typeof PortalAccessModeSchema>;

export const PortalCategorySchema = z.strictObject({
  key: z.string().min(1).max(40),
  label: z.string().min(1).max(80),
});

/**
 * `project.settings`. Every key has a default so `{}` is a valid document; the stored JSON
 * is always the normalized form (parse before insert).
 */
export const ProjectSettingsSchema = z.object({
  /** Word used for work items in this project's UI. */
  terminology: TerminologySchema.default('request'),
  portalAccessMode: PortalAccessModeSchema.default('tenant'),
  portalCategories: z.array(PortalCategorySchema).max(50).default([]),
});
export type ProjectSettings = z.infer<typeof ProjectSettingsSchema>;
export type ProjectSettingsInput = z.input<typeof ProjectSettingsSchema>;

/** PATCH body for settings: keys merge into the stored document. */
export const ProjectSettingsPatchSchema = z
  .strictObject({
    terminology: TerminologySchema,
    portalAccessMode: PortalAccessModeSchema,
    portalCategories: z.array(PortalCategorySchema).max(50),
  })
  .partial();
export type ProjectSettingsPatch = z.infer<typeof ProjectSettingsPatchSchema>;

export const ProjectSchema = z.object({
  id: z.uuid(),
  tenantId: z.uuid(),
  key: ProjectKeySchema,
  name: z.string().min(1).max(120),
  category: z.string().max(80).nullable(),
  leadId: z.uuid().nullable(),
  ticketCounter: z.int().min(0),
  /** Bumped on every configuration change; part of every derived cache key. */
  configVersion: z.int().min(1),
  permissionSchemeId: z.uuid().nullable(),
  settings: ProjectSettingsSchema,
  archivedAt: z.iso.datetime().nullable(),
  /** Set by list/get endpoints: the caller holds `project.admin` here. */
  canAdmin: z.boolean().optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Project = z.infer<typeof ProjectSchema>;

export const CreateProjectSchema = z.strictObject({
  key: ProjectKeySchema,
  name: z.string().min(1).max(120),
  category: z.string().max(80).nullable().optional(),
  leadId: z.uuid().nullable().optional(),
  permissionSchemeId: z.uuid().nullable().optional(),
  settings: ProjectSettingsSchema.prefault({}),
});
export type CreateProject = z.infer<typeof CreateProjectSchema>;

/** The key is renamed through `RenameProjectKeySchema`, never patched. */
export const UpdateProjectSchema = z
  .strictObject({
    name: z.string().min(1).max(120),
    category: z.string().max(80).nullable(),
    leadId: z.uuid().nullable(),
    permissionSchemeId: z.uuid().nullable(),
    settings: ProjectSettingsPatchSchema,
  })
  .partial();
export type UpdateProject = z.infer<typeof UpdateProjectSchema>;

export const RenameProjectKeySchema = z.strictObject({
  key: ProjectKeySchema,
});
export type RenameProjectKey = z.infer<typeof RenameProjectKeySchema>;

export const CreateProjectFromTemplateSchema = z.strictObject({
  templateId: z.uuid(),
  key: ProjectKeySchema,
  name: z.string().min(1).max(120),
});
export type CreateProjectFromTemplate = z.infer<typeof CreateProjectFromTemplateSchema>;

/* ----------------------------- issue types ----------------------------- */

/** Portal presentation of an issue type; every key defaults so `{}` is valid. */
export const IssueTypePortalSchema = z.object({
  visible: z.boolean().default(true),
  /** Portal category key (see `ProjectSettings.portalCategories`). */
  group: z.string().max(40).nullable().default(null),
  helpText: z.string().max(500).nullable().default(null),
});
export type IssueTypePortal = z.infer<typeof IssueTypePortalSchema>;

export const IssueTypePortalPatchSchema = z
  .strictObject({
    visible: z.boolean(),
    group: z.string().max(40).nullable(),
    helpText: z.string().max(500).nullable(),
  })
  .partial();

export const IssueTypeSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  name: z.string().min(1).max(60),
  description: z.string().max(500).nullable(),
  icon: z.string().max(40).nullable(),
  workflowId: z.uuid(),
  isSubtask: z.boolean(),
  position: z.int().min(0),
  portal: IssueTypePortalSchema,
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type IssueType = z.infer<typeof IssueTypeSchema>;

export const CreateIssueTypeSchema = z.strictObject({
  name: z.string().min(1).max(60),
  description: z.string().max(500).nullable().default(null),
  icon: z.string().max(40).nullable().default(null),
  /** Must be a published workflow of the same project (409 `workflow.not_published`). */
  workflowId: z.uuid(),
  isSubtask: z.boolean().default(false),
  /** Appended after the last type when omitted. */
  position: z.int().min(0).optional(),
  portal: IssueTypePortalSchema.prefault({}),
});
export type CreateIssueType = z.infer<typeof CreateIssueTypeSchema>;

export const UpdateIssueTypeSchema = z
  .strictObject({
    name: z.string().min(1).max(60),
    description: z.string().max(500).nullable(),
    icon: z.string().max(40).nullable(),
    workflowId: z.uuid(),
    isSubtask: z.boolean(),
    position: z.int().min(0),
    portal: IssueTypePortalPatchSchema,
  })
  .partial();
export type UpdateIssueType = z.infer<typeof UpdateIssueTypeSchema>;

/* ----------------------------- priorities ----------------------------- */

export const PRIORITY_URGENCIES = ['high', 'low'] as const;
export const PriorityUrgencySchema = z.enum(PRIORITY_URGENCIES);
export type PriorityUrgency = z.infer<typeof PriorityUrgencySchema>;

export const PrioritySchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  name: z.string().min(1).max(40),
  color: ColorSchema.nullable(),
  /** 1 = most urgent; drives `priority.rank` sorting. */
  rank: z.int().min(1).max(1000),
  urgency: PriorityUrgencySchema,
  isDefault: z.boolean(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Priority = z.infer<typeof PrioritySchema>;

export const CreatePrioritySchema = z.strictObject({
  name: z.string().min(1).max(40),
  color: ColorSchema.nullable().default(null),
  rank: z.int().min(1).max(1000),
  urgency: PriorityUrgencySchema,
  /** Setting a new default clears the previous one. */
  isDefault: z.boolean().default(false),
});
export type CreatePriority = z.infer<typeof CreatePrioritySchema>;

export const UpdatePrioritySchema = z
  .strictObject({
    name: z.string().min(1).max(40),
    color: ColorSchema.nullable(),
    rank: z.int().min(1).max(1000),
    urgency: PriorityUrgencySchema,
    isDefault: z.boolean(),
  })
  .partial();
export type UpdatePriority = z.infer<typeof UpdatePrioritySchema>;

/** `PUT .../order`: the complete id list in the new order (priorities, options). */
export const ReorderSchema = z.strictObject({
  ids: z.array(z.uuid()).min(1).max(200),
});
export type Reorder = z.infer<typeof ReorderSchema>;

/**
 * `DELETE .../issue-types/{id}` optional body (also accepted as `?moveTo=`): where tickets of
 * the deleted type go. Required once tickets exist (Bolt 5); recorded as an `issue_type_move` job.
 */
export const DeleteIssueTypeSchema = z.strictObject({
  moveToIssueTypeId: z.uuid().optional(),
});
export type DeleteIssueType = z.infer<typeof DeleteIssueTypeSchema>;
